import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Badge } from '../../ui/badge';
import { createNutritionRequest, type CreateNutritionRequestInput } from '@/data/nutritionRequests';
import { toast } from 'sonner';
import { MessageSquare, Clock } from 'lucide-react';

interface RequestChangesProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assignment: {
    id: string;
    title: string | null;
    effective_snapshot: any;
  };
  onRequestSubmitted: () => void;
}

const REQUEST_TYPES = [
  {
    value: 'portion_change',
    label: 'Portion Size Changes',
    description: 'Request different portion sizes or quantities for existing foods',
  },
  {
    value: 'food_swap',
    label: 'Food Substitutions',
    description: 'Request to swap foods for alternatives (allergies, preferences)',
  },
  {
    value: 'meal_addition',
    label: 'Meal Additions',
    description: 'Request to add new meals or foods to the plan',
  },
  {
    value: 'general_feedback',
    label: 'General Feedback',
    description: 'General questions or feedback about the nutrition plan',
  },
];

export function RequestChanges({ open, onOpenChange, assignment, onRequestSubmitted }: RequestChangesProps) {
  const [requestType, setRequestType] = useState<string>('');
  const [clientMessage, setClientMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmitRequest = async () => {
    if (!requestType || !clientMessage.trim()) {
      toast.error('Please select a request type and describe your request');
      return;
    }

    try {
      setSubmitting(true);

      // For now, use the current snapshot as the requested snapshot
      // In a more advanced implementation, we could let clients make specific changes
      const requestInput: CreateNutritionRequestInput = {
        assignmentId: assignment.id,
        requestType: requestType as any,
        requestedSnapshot: assignment.effective_snapshot, // For now, same as current
        clientMessage: clientMessage.trim(),
      };

      await createNutritionRequest(requestInput);

      toast.success('Your request has been sent to your coach for review');

      // Reset form
      setRequestType('');
      setClientMessage('');

      onRequestSubmitted();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to submit nutrition request:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedRequestType = REQUEST_TYPES.find((rt) => rt.value === requestType);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Request Nutrition Changes
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Plan Info */}
          <div className="p-4 bg-muted rounded-lg">
            <h4 className="font-medium mb-2">Current Plan: {assignment.title}</h4>
            <p className="text-sm text-muted-foreground">
              Request changes to your nutrition plan. Your coach will review and respond to your request.
            </p>
          </div>

          {/* Request Type */}
          <div className="space-y-2">
            <label className="text-sm font-medium">What type of change are you requesting?</label>
            <Select value={requestType} onValueChange={setRequestType}>
              <SelectTrigger>
                <SelectValue placeholder="Select request type..." />
              </SelectTrigger>
              <SelectContent>
                {REQUEST_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div>
                      <div className="font-medium">{type.label}</div>
                      <div className="text-xs text-muted-foreground">{type.description}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Selected Type Info */}
          {selectedRequestType && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="secondary">{selectedRequestType.label}</Badge>
              </div>
              <p className="text-sm text-blue-700">{selectedRequestType.description}</p>
            </div>
          )}

          {/* Message */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Describe your request</label>
            <Textarea
              value={clientMessage}
              onChange={(e) => setClientMessage(e.target.value)}
              placeholder="Please describe what changes you'd like to make to your nutrition plan. Be as specific as possible to help your coach understand your needs."
              rows={4}
              className="resize-none"
            />
            <div className="text-xs text-muted-foreground">{clientMessage.length}/500 characters</div>
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitRequest}
              disabled={!requestType || !clientMessage.trim() || submitting}
              className="flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <Clock className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <MessageSquare className="w-4 h-4" />
                  Submit Request
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
